import React, { useEffect, useRef, useState } from "react";
import "./HeroSection.css";
import { Link as ScrollLink } from "react-scroll";
import { FaArrowRight, FaPlay } from "react-icons/fa";
import useInView from "../hooks/useInView";
import useCountUp from "../hooks/useCountUp";

/* Word-by-word reveal heading */
const RevealHeading = ({ words }) => {
  return (
    <h1 className="hero-heading">
      {words.map((w, i) => (
        <span className="word" key={i} style={{ animationDelay: `${i * 0.09}s` }}>
          <span className="word-inner">
            {w.accent ? <span className="accent">{w.text}</span> : w.text}
          </span>
        </span>
      ))}
    </h1>
  );
};

const Stat = ({ value, suffix, label, inView }) => {
  const n = useCountUp(value, inView, 2000);
  return (
    <div className="stat">
      <strong>
        {n}
        {suffix}
      </strong>
      <span>{label}</span>
    </div>
  );
};

const HeroSection = () => {
  const heroRef = useRef(null);
  const [statsRef, statsInView] = useInView();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  // Mouse-driven parallax for the mesh blobs
  useEffect(() => {
    const handle = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setMouse({
        x: (e.clientX - w / 2) / w,
        y: (e.clientY - h / 2) / h,
      });
    };
    window.addEventListener("mousemove", handle, { passive: true });
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  const words = [
    { text: "Precision", accent: true },
    { text: "in", accent: true },
    { text: "Design," , accent: true},
    { text: "Estimation", accent: true },
    { text: "&", accent: true },
    { text: "Real", accent: true },
    { text: "Estate", accent: true },
  ];

  return (
    <section id="home" className="hero" ref={heroRef}>
      {/* Animated cinematic background */}
      <div className="hero-mesh">
        <span
          className="blob blob-1"
          style={{ transform: `translate(${mouse.x * -40}px, ${mouse.y * -40}px)` }}
        />
        <span
          className="blob blob-2"
          style={{ transform: `translate(${mouse.x * 50}px, ${mouse.y * 30}px)` }}
        />
        <span
          className="blob blob-3"
          style={{ transform: `translate(${mouse.x * 30}px, ${mouse.y * -50}px)` }}
        />
        <span
          className="blob blob-4"
          style={{ transform: `translate(${mouse.x * -25}px, ${mouse.y * 35}px)` }}
        />
      </div>

      <div className="hero-grid" />
      <div className="hero-vignette" />

      {/* Floating tech glyphs */}
      <div className="hero-glyphs">
        <span className="glyph g1">{"</>"}</span>
        <span className="glyph g2">{"{ }"}</span>
        <span className="glyph g3">⌬</span>
        <span className="glyph g4">▲</span>
        <span className="glyph g5">●</span>
      </div>

      <div className="hero-content">
        <div className="subheading">
          <span className="pulse-dot" />
          Vezron Technologies Pvt. Ltd.
          <span className="divider-dot">•</span>
          <span className="badge-meta">India × USA</span>
        </div>

        <RevealHeading words={words} />

        <p className="hero-sub fade-up" style={{ animationDelay: "0.95s" }}>
          Helping builders, developers, and real-estate teams scale with
          accurate cost estimation, precision drafting, and expert brokerage
          — <span className="line-emphasis">delivered on time, every time.</span>
        </p>

        <div className="hero-cta-group fade-up" style={{ animationDelay: "1.1s" }}>
          <ScrollLink
            to="contact"
            smooth={true}
            duration={500}
            offset={-70}
            className="cta-button primary"
          >
            <span>Get Free Consultation</span>
            <FaArrowRight />
          </ScrollLink>
          <ScrollLink
            to="services"
            smooth={true}
            duration={500}
            offset={-70}
            className="cta-button secondary"
          >
            <span className="play-icon"><FaPlay /></span>
            <span>Explore Services</span>
          </ScrollLink>
        </div>

        <div
          ref={statsRef}
          className="hero-stats fade-up"
          style={{ animationDelay: "1.3s" }}
        >
          <Stat value={500} suffix="+" label="Projects Delivered" inView={statsInView} />
          <div className="stat-divider" />
          <Stat value={50} suffix="+" label="Happy Clients" inView={statsInView} />
          <div className="stat-divider" />
          <Stat value={5} suffix="+" label="Years Experience" inView={statsInView} />
        </div>
      </div>

      <div className="scroll-cue" aria-hidden="true">
        <span className="cue-track">
          <span className="cue-dot" />
        </span>
        <span className="cue-label">SCROLL</span>
      </div>
    </section>
  );
};

export default HeroSection;
